import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3, PieChart as PieChartIcon, TrendingUp } from 'lucide-react';
import SiteBrandedLoader from './SiteBrandedLoader';

export type DashboardAnalyticsPayload = {
  rangeDays: number;
  from: string;
  to: string;
  revenueByDay: { date: string; totalToman: number }[];
  enrollmentsByDay: { date: string; count: number }[];
  newUsersByDay: { date: string; count: number }[];
  leadsByDay: { date: string; count: number }[];
  invoicesByStatus: { status: string; count: number }[];
  topCoursesByEnrollment: { courseId: string; title: string; enrollments: number }[];
  summary: {
    revenueToman: number;
    enrollments: number;
    newUsers: number;
    newLeads: number;
  };
};

const STATUS_FA: Record<string, string> = {
  PAID: 'پرداخت‌شده',
  PENDING: 'در انتظار',
  CREATED: 'ایجاد شده',
  GATEWAY_REDIRECTED: 'هدایت به درگاه',
  PAYMENT_TIMEOUT: 'اتمام زمان',
  FAILED: 'ناموفق',
  CANCELLED: 'لغو شده',
};

const PIE_COLORS = ['#1C3B9B', '#C11277', '#0d9488', '#d97706', '#64748b', '#7c3aed', '#dc2626', '#059669'];

function shortFaDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map((x) => Number(x));
  if (!y || !m || !d) return isoDate;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('fa-IR', { month: 'numeric', day: 'numeric' });
}

function formatTomans(n: number): string {
  return `${Math.round(n).toLocaleString('fa-IR')} تومان`;
}

const chartTooltipStyle = {
  backgroundColor: 'rgba(255,255,255,0.96)',
  border: '1px solid rgb(226 232 240)',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
};

type Props = {
  data: DashboardAnalyticsPayload | null;
  loading: boolean;
  error: string;
  rangeDays: number;
  onRangeDaysChange: (d: 14 | 30) => void;
};

const DashboardAnalyticsSection: React.FC<Props> = ({
  data,
  loading,
  error,
  rangeDays,
  onRangeDaysChange,
}) => {
  const combined = useMemo(() => {
    if (!data?.revenueByDay?.length) return [];
    return data.revenueByDay.map((r, i) => ({
      date: r.date,
      label: shortFaDate(r.date),
      revenue: r.totalToman,
      enroll: data.enrollmentsByDay[i]?.count ?? 0,
      newUsers: data.newUsersByDay[i]?.count ?? 0,
      leads: data.leadsByDay[i]?.count ?? 0,
    }));
  }, [data]);

  const pieData = useMemo(() => {
    if (!data?.invoicesByStatus?.length) return [];
    return data.invoicesByStatus
      .filter((x) => x.count > 0)
      .map((x) => ({
        name: STATUS_FA[x.status] || x.status,
        value: x.count,
        key: x.status,
      }));
  }, [data]);

  const topCoursesChart = useMemo(() => {
    if (!data?.topCoursesByEnrollment?.length) return [];
    return [...data.topCoursesByEnrollment]
      .reverse()
      .map((c) => ({
        name:
          c.title.length > 28 ? `${c.title.slice(0, 26)}…` : c.title,
        fullTitle: c.title,
        enrollments: c.enrollments,
      }));
  }, [data]);

  const hasActivity =
    data &&
    (data.summary.revenueToman > 0 ||
      data.summary.enrollments > 0 ||
      data.summary.newUsers > 0 ||
      data.summary.newLeads > 0);

  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80 sm:p-6" dir="rtl">
      <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-indigo-700">
            <BarChart3 className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
            <h2 className="text-lg font-black text-slate-900">تحلیل و روند</h2>
          </div>
          <p className="mt-1 text-sm font-medium text-slate-600">
            فروش پرداخت‌شده، ثبت‌نام، کاربران و لیدها در بازه انتخابی؛ توزیع وضعیت فاکتور و پرفروش‌ترین دوره‌ها.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/80 p-1">
          {([14, 30] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onRangeDaysChange(d)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                rangeDays === d
                  ? 'bg-white text-indigo-800 shadow-sm ring-1 ring-slate-200/80'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {d.toLocaleString('fa-IR')} روز
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex min-h-[14rem] items-center justify-center py-10">
          <SiteBrandedLoader variant="embedded" message="در حال بارگذاری نمودارها…" className="min-h-0 bg-transparent py-0" />
        </div>
      ) : error ? (
        <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">
          {error}
        </div>
      ) : !data ? null : (
        <>
          {!hasActivity ? (
            <p className="mt-4 rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3 text-sm font-medium text-amber-900">
              در این بازه داده‌ای برای رسم نمودار نیست؛ با گسترش بازه یا بعد از فعالیت کاربران دوباره بررسی کنید.
            </p>
          ) : null}

          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { label: 'فروش پرداخت‌شده', value: formatTomans(data.summary.revenueToman), tone: 'text-indigo-800' },
              { label: 'ثبت‌نام دوره', value: data.summary.enrollments.toLocaleString('fa-IR'), tone: 'text-emerald-800' },
              { label: 'کاربر جدید', value: data.summary.newUsers.toLocaleString('fa-IR'), tone: 'text-violet-800' },
              { label: 'لید جدید', value: data.summary.newLeads.toLocaleString('fa-IR'), tone: 'text-rose-800' },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-slate-100 bg-gradient-to-b from-slate-50/80 to-white px-3 py-3 shadow-sm"
              >
                <p className="text-[11px] font-bold text-slate-500">{s.label}</p>
                <p className={`mt-1 text-lg font-black tabular-nums ${s.tone}`}>{s.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-8 xl:grid-cols-2">
            <div className="min-h-[300px] rounded-2xl border border-slate-100 bg-slate-50/40 p-4">
              <div className="mb-3 flex items-center gap-2 text-slate-800">
                <TrendingUp className="h-4 w-4 text-indigo-600" strokeWidth={2} aria-hidden />
                <h3 className="text-sm font-black">فروش روزانه (تومان)</h3>
              </div>
              <div className="h-[260px] w-full min-w-0" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={combined} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1C3B9B" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#1C3B9B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#64748b' }}
                      tickFormatter={(v) => Number(v).toLocaleString('fa-IR')}
                      width={56}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={chartTooltipStyle}
                      formatter={(value: number) => [formatTomans(Number(value)), 'فروش']}
                      labelFormatter={(l) => `تاریخ: ${l}`}
                    />
                    <Area type="monotone" dataKey="revenue" stroke="#1C3B9B" strokeWidth={2} fill="url(#revFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="min-h-[300px] rounded-2xl border border-slate-100 bg-slate-50/40 p-4">
              <div className="mb-3 flex items-center gap-2 text-slate-800">
                <BarChart3 className="h-4 w-4 text-emerald-600" strokeWidth={2} aria-hidden />
                <h3 className="text-sm font-black">فعالیت روزانه</h3>
              </div>
              <p className="mb-2 text-[11px] font-medium text-slate-500">ثبت‌نام دوره، کاربر جدید، لید</p>
              <div className="h-[260px] w-full min-w-0" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={combined} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#64748b' }} width={36} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={chartTooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 700 }} />
                    <Bar dataKey="enroll" name="ثبت‌نام" fill="#0d9488" radius={[4, 4, 0, 0]} maxBarSize={14} />
                    <Bar dataKey="newUsers" name="کاربر جدید" fill="#7c3aed" radius={[4, 4, 0, 0]} maxBarSize={14} />
                    <Bar dataKey="leads" name="لید" fill="#e11d48" radius={[4, 4, 0, 0]} maxBarSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="min-h-[280px] rounded-2xl border border-slate-100 bg-slate-50/40 p-4">
              <div className="mb-2 flex items-center gap-2 text-slate-800">
                <PieChartIcon className="h-4 w-4 text-rose-600" strokeWidth={2} aria-hidden />
                <h3 className="text-sm font-black">فاکتورها بر اساس وضعیت</h3>
              </div>
              <p className="mb-2 text-[11px] font-medium text-slate-500">کل دوره، نه فقط بازه بالا</p>
              <div className="h-[240px] w-full min-w-0" dir="ltr">
                {pieData.length === 0 ? (
                  <p className="py-16 text-center text-sm text-slate-500">فاکتوری ثبت نشده است.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={52}
                        outerRadius={88}
                        paddingAngle={2}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={entry.key} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        formatter={(value: number) => [value.toLocaleString('fa-IR'), 'تعداد']}
                      />
                      <Legend wrapperStyle={{ fontSize: '11px', fontWeight: 600 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="min-h-[280px] rounded-2xl border border-slate-100 bg-slate-50/40 p-4">
              <div className="mb-2 flex items-center gap-2 text-slate-800">
                <BarChart3 className="h-4 w-4 text-amber-600" strokeWidth={2} aria-hidden />
                <h3 className="text-sm font-black">پرفروش‌ترین دوره‌ها</h3>
              </div>
              <p className="mb-2 text-[11px] font-medium text-slate-500">بر اساس ثبت‌نام در همین بازه</p>
              <div className="h-[240px] w-full min-w-0" dir="ltr">
                {topCoursesChart.length === 0 ? (
                  <p className="py-16 text-center text-sm text-slate-500">ثبت‌نامی در این بازه نیست.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topCoursesChart} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={108}
                        tick={{ fontSize: 9, fill: '#475569' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={chartTooltipStyle}
                        formatter={(value: number) => [value.toLocaleString('fa-IR'), 'ثبت‌نام']}
                        labelFormatter={(_, p) => (p?.[0]?.payload?.fullTitle as string) || ''}
                      />
                      <Bar dataKey="enrollments" fill="#d97706" radius={[0, 6, 6, 0]} barSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardAnalyticsSection;
